import { useEffect, useRef } from "react"

import debounce from "@utils/debounce"

/**
 * Elements currently tracking each CSS variable, newest last. Several can track the same variable at once
 * (stacked drawers, two views mid-transition), and only the newest one writes the value.
 */
const heightPublishersByVariable = new Map<string, Array<() => void>>()

/**
 * Hook that creates a ref, tracks the element's height, and sets it as a CSS variable on the document root
 * @param cssVariableName - Name of the CSS variable to set (e.g., "--popover-header-height")
 * @param condition - Optional condition to re-calculate height when they change
 * @returns A ref to attach to the element you want to track
 */
export default function useHeightCSSVariable<T extends HTMLElement = HTMLDivElement>(
    cssVariableName: string,
    condition: boolean = true,
) {
    const elementRef = useRef<T>(null)

    useEffect(() => {
        const element = elementRef.current

        if (!element || !condition) {
            return
        }

        const publishers = heightPublishersByVariable.get(cssVariableName) ?? []
        heightPublishersByVariable.set(cssVariableName, publishers)

        const publishHeight = () => {
            document.documentElement.style.setProperty(cssVariableName, `${element.clientHeight}px`)
        }

        const setHeight = () => {
            if (publishers[publishers.length - 1] === publishHeight) {
                publishHeight()
            }
        }

        // Set initial height immediately
        publishers.push(publishHeight)
        setHeight()

        // Debounce subsequent resize observations to avoid excessive updates
        const RESIZE_CALLBACK_DEBOUNCE_TIME = 100
        const resizeObserver = new ResizeObserver(debounce(setHeight, RESIZE_CALLBACK_DEBOUNCE_TIME))
        resizeObserver.observe(element)

        return () => {
            resizeObserver.disconnect()
            publishers.splice(publishers.indexOf(publishHeight), 1)

            // Hand the variable back to the element that was tracking it before this one, if any
            const previousPublisher = publishers[publishers.length - 1]

            if (previousPublisher) {
                previousPublisher()
            } else {
                heightPublishersByVariable.delete(cssVariableName)
                document.documentElement.style.removeProperty(cssVariableName)
            }
        }
    }, [cssVariableName, condition])

    return elementRef
}
